import { useState, useEffect } from 'react';
import axios from 'axios';
import { UserMinus, Loader2, AlertTriangle, CheckCircle2, RotateCcw, Search } from 'lucide-react';

const API = '/api';

// Fitter Offboarding — preview -> confirm. Sets status + prunes groups + stops
// routing + reassigns leads + kicks off the return. Drafts only; nothing auto-sends.

const EXITS = [
  { id: 'departed', label: 'Departed', desc: 'Full exit: groups pruned, routing off, leads reassigned, equipment return started, thank-you drafted.' },
  { id: 'retired_with_system', label: 'Retired (keeps system)', desc: 'Same as Departed but no equipment return.' },
  { id: 'on_leave', label: 'On leave (pause)', desc: 'Check-ins + routing paused, links invalidated; stays on Weekly Fitter + T2T. Reversible.' },
];

export default function Offboarding() {
  const [fitters, setFitters] = useState([]);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(null);
  const [exitType, setExitType] = useState('departed');
  const [reassign, setReassign] = useState('');
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/fitters`).then((r) => setFitters(r.data.fitters || [])).catch(() => {});
  }, []);

  const matches = q.trim()
    ? fitters.filter((f) => (f.name || '').toLowerCase().includes(q.toLowerCase())
        || (f.primary_email || '').toLowerCase().includes(q.toLowerCase())).slice(0, 8)
    : [];
  const activeFitters = fitters.filter((f) => f.status === 'active' && !f.is_test);

  const doPreview = async () => {
    setBusy(true); setError(''); setResult(null);
    try {
      const r = await axios.post(`${API}/offboard/preview`, {
        fitter_id: sel.id, exit_type: exitType,
        reassign_to_name: reassign || null, note: note || null,
      });
      setPreview(r.data);
    } catch (e) { setError(e.response?.data?.detail || 'Preview failed.'); }
    setBusy(false);
  };

  const doCommit = async () => {
    if (!window.confirm(`Offboard ${sel.name} as ${exitType.replace(/_/g, ' ').toUpperCase()}? The summary below is exactly what will happen.`)) return;
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${API}/offboard/commit`, {
        fitter_id: sel.id, exit_type: exitType,
        reassign_to_name: reassign || null, note: note || null,
      });
      setResult(r.data); setPreview(null);
    } catch (e) { setError(e.response?.data?.detail || 'Offboard failed.'); }
    setBusy(false);
  };

  const doReactivate = async (f) => {
    if (!window.confirm(`Reactivate ${f.name}? Check-ins, routing and lead links come back on.`)) return;
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${API}/offboard/reactivate`, { fitter_id: f.id });
      setResult({ fitter: f.name, reactivated: r.data }); setSel(null);
      const list = await axios.get(`${API}/fitters`); setFitters(list.data.fitters || []);
    } catch (e) { setError(e.response?.data?.detail || 'Reactivate failed.'); }
    setBusy(false);
  };

  const onLeave = fitters.filter((f) => f.status === 'on_leave');

  return (
    <div className="space-y-5" data-testid="offboarding-page">
      <div>
        <h1 className="font-display text-2xl text-hg-text tracking-wider inline-flex items-center gap-2">
          <UserMinus size={20} className="text-hg-green" /> FITTER OFFBOARDING
        </h1>
        <p className="text-sm text-hg-text-dim font-sans mt-1">
          Preview first, then confirm. Every change is logged; the thank-you and return emails are drafts — nothing sends automatically.
        </p>
      </div>

      {onLeave.length > 0 && (
        <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-2 max-w-2xl">
          <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">On leave</div>
          {onLeave.map((f) => (
            <div key={f.id} className="flex items-center justify-between text-xs font-sans text-hg-text">
              <span>{f.name} · {f.primary_email}</span>
              <button onClick={() => doReactivate(f)} disabled={busy}
                className="inline-flex items-center gap-1 px-2.5 py-1 border border-hg-green text-hg-green rounded-sm hover:bg-hg-green/10">
                <RotateCcw size={11} /> Reactivate
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="bg-hg-surface border border-hg-border rounded-sm p-5 space-y-4 max-w-2xl">
        {!sel ? (
          <div>
            <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1">Find the fitter</label>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-2.5 text-hg-text-dim" />
              <input data-testid="offboard-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or email…"
                className="w-full bg-hg-bg border border-hg-border rounded-sm pl-8 pr-3 py-2 text-sm text-hg-text font-sans" />
            </div>
            {matches.map((f) => (
              <button key={f.id} onClick={() => { setSel(f); setQ(''); setPreview(null); setResult(null); }}
                className="block w-full text-left px-3 py-2 text-xs font-sans text-hg-text hover:bg-hg-bg border-b border-hg-border/40">
                {f.name} <span className="text-hg-text-dim">· {f.primary_email} · {f.status}{f.is_test ? ' · TEST' : ''}</span>
              </button>
            ))}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div className="text-sm font-sans text-hg-text font-medium">{sel.name}
                <span className="text-hg-text-dim font-normal"> · {sel.primary_email} · currently {sel.status}</span></div>
              <button onClick={() => { setSel(null); setPreview(null); }} className="text-xs font-sans text-hg-text-dim hover:text-hg-text underline">change</button>
            </div>

            <div className="space-y-2">
              {EXITS.map((x) => (
                <label key={x.id} className={`block border rounded-sm p-3 cursor-pointer ${exitType === x.id ? 'border-hg-green bg-hg-green/5' : 'border-hg-border'}`}>
                  <div className="flex items-center gap-2 text-sm font-sans text-hg-text">
                    <input type="radio" checked={exitType === x.id} onChange={() => { setExitType(x.id); setPreview(null); }} /> {x.label}
                  </div>
                  <div className="text-[11px] font-sans text-hg-text-dim mt-1 ml-5">{x.desc}</div>
                </label>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1">
                  Reassign open leads to {exitType === 'on_leave' ? '(optional)' : '(required if leads open)'}
                </label>
                <input list="active-fitters" value={reassign} onChange={(e) => { setReassign(e.target.value); setPreview(null); }}
                  placeholder="Fitter taking over…" data-testid="offboard-reassign"
                  className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans" />
                <datalist id="active-fitters">
                  {activeFitters.map((f) => <option key={f.id} value={f.name} />)}
                </datalist>
              </div>
              <div>
                <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1">Note (goes in the log)</label>
                <input value={note} onChange={(e) => setNote(e.target.value)}
                  className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans" />
              </div>
            </div>

            <button onClick={doPreview} disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2 text-xs font-sans border border-hg-green text-hg-green rounded-sm hover:bg-hg-green/10 disabled:opacity-40"
              data-testid="offboard-preview">
              {busy ? <Loader2 size={13} className="animate-spin" /> : null} Preview what will happen
            </button>
          </>
        )}
        {error && <div className="text-xs text-red-400 font-sans flex items-center gap-1"><AlertTriangle size={12} /> {error}</div>}
      </div>

      {preview && (
        <div className="bg-hg-surface border border-amber-400/40 rounded-sm p-5 space-y-3 max-w-2xl" data-testid="offboard-summary">
          <div className="text-[10px] font-sans uppercase tracking-wider text-amber-300">Confirmation summary — nothing has happened yet</div>
          <ul className="text-xs font-sans text-hg-text space-y-1.5 list-disc pl-5">
            <li>Status: {preview.fitter.status} → <b>{preview.will.set_status}</b>; check-ins OFF</li>
            <li>MailerLite removals: {Object.entries(preview.will.mailerlite_remove).map(([em, gs]) =>
              gs.length ? `${em}: ${gs.join(', ')}` : `${em}: none`).join(' · ')}</li>
            <li>Kept (always): HG Newsletter — T2T{exitType === 'on_leave' ? ' + Weekly Fitter (pause keeps groups)' : ''}</li>
            <li>Lead routing off: {preview.will.deactivate_promoted.length ? preview.will.deactivate_promoted.join(', ') : 'no active Promote-Fitters records'}</li>
            <li>One-tap links invalidated on {preview.will.invalidate_status_links_on_leads} open lead(s);
              reassignment: {preview.will.reassign_open_leads.to || preview.will.reassign_open_leads.note || 'none'}</li>
            <li>Equipment return: {preview.will.start_equipment_return ? 'will start (awaiting send — clock starts when the email is sent)' :
              preview.will.existing_open_return ? `already open (${preview.will.existing_open_return.status})` : 'not applicable'}</li>
            <li>Thank-you email: {preview.will.draft_thank_you ? 'DRAFTED for review (not sent)' : 'none'}</li>
          </ul>
          {preview.open_leads.length > 0 && (
            <div className="text-[11px] font-sans text-hg-text-dim">
              Open leads: {preview.open_leads.map((l) => `${l.name} (${l.status})`).join(', ')}
            </div>
          )}
          <button onClick={doCommit} disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40"
            data-testid="offboard-commit">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Confirm offboard
          </button>
        </div>
      )}

      {result && (
        <div className="bg-hg-surface border border-hg-green/40 rounded-sm p-5 max-w-2xl space-y-2" data-testid="offboard-result">
          <div className="text-sm font-sans text-hg-green inline-flex items-center gap-2"><CheckCircle2 size={14} /> Done — every step logged to the lifecycle log.</div>
          <pre className="text-[10px] font-mono text-hg-text-dim whitespace-pre-wrap overflow-x-auto">{JSON.stringify(result, null, 1)}</pre>
        </div>
      )}
    </div>
  );
}
